import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';
import { iconDownload, iconLogout, iconMore, iconTrash } from './icons';

/**
 * `<embrace-settings-menu>` — dropdown-actions-menu rechtsboven in de
 * header. Toont alleen de acties die op dit moment van toepassing zijn:
 *
 *   - **Download transcript** — wanneer `canDownloadTranscript`
 *   - **Chatgesprek sluiten** — wanneer `canCloseConversation` (lopende sessie)
 *   - **Mij vergeten** — wanneer er een persistente visitor is (firstName + email)
 *
 * Geen wijzig-flow meer — bezoeker kan z'n gegevens vergeten en bij
 * volgende bezoek opnieuw invoeren. Eenvoudiger UX dan een mini-form.
 *
 * Twee modi: `closed` (alleen trigger-knop) en `menu` (popover open).
 * "Mij vergeten" opent een confirm-dialog (`confirm`-mode) want het is
 * destructief en cascadet de lopende sessie weg.
 */
@customElement('embrace-settings-menu')
export class EmbraceSettingsMenu extends LitElement {
  /** Visitor-naam — alleen gebruikt in de confirm-dialog tekst. */
  @property({ attribute: false })
  firstName = '';

  /** Visitor-email — alleen gebruikt in de confirm-dialog tekst. */
  @property({ attribute: false })
  email = '';

  /** Toont "Download transcript"-actie wanneer transcript beschikbaar. */
  @property({ type: Boolean })
  canDownloadTranscript = false;

  /** Toont "Chatgesprek sluiten"-actie wanneer er een lopende sessie is. */
  @property({ type: Boolean })
  canCloseConversation = false;

  @state()
  private mode: 'closed' | 'menu' | 'confirm' = 'closed';

  override connectedCallback(): void {
    super.connectedCallback();
    // Document-level listener voor klik-buiten-sluit. composedPath()
    // pierced de Shadow DOM zodat we ook clicks op andere custom-elements
    // correct als "buiten" herkennen. Capture-fase voorkomt dat een
    // onderliggende `stopPropagation` ons signaal slikt.
    document.addEventListener('click', this.onDocumentClick, true);
    // Escape-toets sluit ook — toetsenbord-bezoekers verwachten dit voor
    // popovers.
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    super.disconnectedCallback();
  }

  private readonly onDocumentClick = (e: MouseEvent): void => {
    if (this.mode === 'closed') return;
    // composedPath bevat alle ancestors door Shadow DOM heen. Als de
    // klik binnen onszelf zit, is het géén "outside click" — laat de
    // interne handlers (toggle, action-rows, close-x) hun werk doen.
    if (e.composedPath().includes(this)) return;
    this.mode = 'closed';
  };

  private readonly onDocumentKeydown = (e: KeyboardEvent): void => {
    if (this.mode !== 'closed' && e.key === 'Escape') {
      this.mode = 'closed';
    }
  };

  static override styles = css`
    :host {
      position: relative;
      display: inline-block;
    }

    .trigger {
      background: transparent;
      border: 0;
      padding: 6px;
      cursor: pointer;
      color: var(--ec-on-primary);
      opacity: .7;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background var(--ec-duration-fast) var(--ec-ease-smooth),
                  opacity var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .trigger:hover {
      background: rgba(255, 255, 255, 0.12);
      opacity: 1;
    }
    .trigger:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }

    .popover {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      min-width: 240px;
      background: #fff;
      color: var(--ec-text);
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(12, 28, 71, 0.18);
      border: 1px solid #e6e7ea;
      padding: 8px;
      z-index: 10;
    }

    .title {
      font: 700 13px/1.3 var(--ec-font-display);
      margin: 6px 8px 8px;
    }
    .hint {
      font: 400 12px/1.4 var(--ec-font-family);
      color: var(--ec-text-muted);
      margin: 0 8px 8px;
    }

    .action-row {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 10px 8px;
      background: transparent;
      border: 0;
      border-radius: 8px;
      cursor: pointer;
      color: var(--ec-text);
      font: 500 13.5px/1.3 var(--ec-font-family);
      text-align: left;
    }
    .action-row:hover {
      background: #f7f8fa;
    }
    .action-row:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }
    .action-row.danger {
      color: var(--ec-error);
    }
    .action-row.danger:hover {
      background: var(--ec-error-subtle);
    }
    .action-icon {
      flex: 0 0 auto;
      color: var(--ec-text-muted);
      display: grid;
      place-items: center;
    }
    .action-row.danger .action-icon {
      color: var(--ec-error);
    }

    .actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-top: 8px;
    }
    .btn {
      border: 0;
      padding: 7px 12px;
      font: 600 12.5px/1.2 var(--ec-font-family);
      border-radius: 7px;
      cursor: pointer;
    }
    .btn-secondary {
      background: transparent;
      color: var(--ec-text-muted);
    }
    .btn-secondary:hover { color: var(--ec-text); }
    .btn-danger {
      background: var(--ec-error);
      color: var(--ec-on-error);
    }
    .btn-danger:hover { filter: brightness(0.95); }
  `;

  override render() {
    return html`
      <button
        class="trigger"
        type="button"
        aria-label=${i18n.settings.btnOpen}
        aria-haspopup="menu"
        aria-expanded=${this.mode !== 'closed'}
        @click=${this.toggleMenu}
      >
        ${iconMore()}
      </button>
      ${this.renderPopover()}
    `;
  }

  private renderPopover() {
    if (this.mode === 'closed') return nothing;
    // Geen losse close-X — click-outside + Escape handelen dismissal af.
    // Confirm-modus heeft eigen "Annuleer"-knop voor expliciete cancel.
    return html`
      <div class="popover" role="dialog" aria-label=${i18n.settings.title}>
        ${this.mode === 'confirm' ? this.renderConfirm() : this.renderMenu()}
      </div>
    `;
  }

  private renderMenu() {
    const hasVisitor = this.firstName !== '' && this.email !== '';
    const noActions =
      !this.canDownloadTranscript && !this.canCloseConversation && !hasVisitor;
    return html`
      ${noActions
        ? html`<p class="hint">${i18n.settings.emptyHint}</p>`
        : nothing}
      ${this.canDownloadTranscript
        ? html`
            <button
              class="action-row"
              type="button"
              part="action-download"
              @click=${this.onDownloadClick}
            >
              <span class="action-icon">${iconDownload()}</span>
              <span>${i18n.settings.btnDownload}</span>
            </button>
          `
        : nothing}
      ${this.canCloseConversation
        ? html`
            <button
              class="action-row"
              type="button"
              part="action-close"
              @click=${this.onCloseConversationClick}
            >
              <span class="action-icon">${iconLogout()}</span>
              <span>${i18n.settings.btnCloseConversation}</span>
            </button>
          `
        : nothing}
      ${hasVisitor
        ? html`
            <button
              class="action-row danger"
              type="button"
              part="action-forget"
              @click=${this.openConfirm}
            >
              <span class="action-icon">${iconTrash()}</span>
              <span>${i18n.settings.btnForget}</span>
            </button>
          `
        : nothing}
    `;
  }

  private renderConfirm() {
    const subject =
      this.firstName !== '' || this.email !== ''
        ? `${this.firstName} (${this.email})`
        : '';
    return html`
      <p class="title">${i18n.settings.confirmTitle}</p>
      <p class="hint">${i18n.settings.confirmBody(subject)}</p>
      <div class="actions">
        <button class="btn btn-secondary" type="button" @click=${this.cancelConfirm}>
          ${i18n.settings.confirmCancel}
        </button>
        <button class="btn btn-danger" type="button" @click=${this.confirmForget}>
          ${i18n.settings.confirmYes}
        </button>
      </div>
    `;
  }

  // ─── Event-handlers ─────────────────────────────────────────────────────

  private toggleMenu = (): void => {
    this.mode = this.mode === 'closed' ? 'menu' : 'closed';
  };

  private close = (): void => {
    this.mode = 'closed';
  };

  private openConfirm = (): void => {
    this.mode = 'confirm';
  };

  private cancelConfirm = (): void => {
    this.mode = 'menu';
  };

  private confirmForget = (): void => {
    this.dispatchEvent(
      new CustomEvent('visitor-forget', {
        bubbles: true,
        composed: true,
      }),
    );
    this.close();
  };

  private onDownloadClick = (): void => {
    this.dispatchEvent(
      new CustomEvent('download-transcript', {
        bubbles: true,
        composed: true,
      }),
    );
    this.close();
  };

  private onCloseConversationClick = (): void => {
    this.dispatchEvent(
      new CustomEvent('end-conversation', {
        bubbles: true,
        composed: true,
      }),
    );
    this.close();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-settings-menu': EmbraceSettingsMenu;
  }
}
